import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Bot, CheckCircle2, Globe2, KeyRound, RefreshCw, Save, ShieldCheck, Trash2 } from 'lucide-react';

const FALLBACK_DEFAULTS = {
    yearMin: 2015,
    yearMax: new Date().getFullYear(),
    excludedStudios: ['Podium Audio', 'Aethon Audio', 'Audible Studios', 'Blackstone Publishing', 'Mountaindale Press'],
    preferredStudios: ['Tantor Audio', 'Royal Guard Publishing', 'Soundbooth Theater'],
    genres: ['LitRPG', 'Post Apocalyptic', 'Sci-Fi', 'Fantasy', 'Thriller', 'Zombies'],
    maxResults: 18,
    randomRotationHours: 12
};

function listToText(value) {
    return Array.isArray(value) ? value.join('\n') : '';
}

function textToList(value) {
    return [...new Set(String(value || '').split(/\n|,/).map(item => item.trim()).filter(Boolean))];
}

export default function HopeLabSettings({ firebaseUser }) {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [configured, setConfigured] = useState(false);
    const [maskedApiKey, setMaskedApiKey] = useState('');
    const [apiKey, setApiKey] = useState('');
    const [model, setModel] = useState('gemini-2.5-flash');
    const [embeddingModel, setEmbeddingModel] = useState('text-embedding-3-small');
    const [models, setModels] = useState([]);
    const [webSearchEnabled, setWebSearchEnabled] = useState(true);
    const [defaults, setDefaults] = useState(FALLBACK_DEFAULTS);
    const [excludedStudios, setExcludedStudios] = useState(listToText(FALLBACK_DEFAULTS.excludedStudios));
    const [preferredStudios, setPreferredStudios] = useState(listToText(FALLBACK_DEFAULTS.preferredStudios));

    const apiFetch = useCallback(async (url, options = {}) => {
        if (!firebaseUser) throw new Error('Vui lòng đăng nhập lại để tiếp tục.');
        const send = async forceRefresh => {
            const token = await firebaseUser.getIdToken(forceRefresh);
            return fetch(url, {
                ...options,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                    ...(options.headers || {})
                }
            });
        };
        let response = await send(false);
        if (response.status === 401) response = await send(true);
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || (response.status === 401 ? 'Phiên đăng nhập không còn hợp lệ. Vui lòng đăng nhập lại.' : `HTTP ${response.status}`));
        return payload;
    }, [firebaseUser]);

    const loadSettings = useCallback(async () => {
        if (!firebaseUser) return;
        setLoading(true);
        setError('');
        try {
            const payload = await apiFetch('/api/hopelab/settings');
            const nextDefaults = { ...FALLBACK_DEFAULTS, ...(payload.defaults || {}) };
            setConfigured(Boolean(payload.configured));
            setMaskedApiKey(payload.maskedApiKey || '');
            setModel(payload.model || 'gemini-2.5-flash');
            setEmbeddingModel(payload.embeddingModel || 'text-embedding-3-small');
            setModels(payload.models || []);
            setWebSearchEnabled(payload.webSearchEnabled !== false);
            setDefaults(nextDefaults);
            setExcludedStudios(listToText(nextDefaults.excludedStudios));
            setPreferredStudios(listToText(nextDefaults.preferredStudios));
            if (payload.modelsError) setError(`Không tải được catalog model: ${payload.modelsError}`);
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setLoading(false);
        }
    }, [apiFetch, firebaseUser]);

    useEffect(() => {
        if (firebaseUser) loadSettings();
    }, [firebaseUser, loadSettings]);

    const selectedModel = useMemo(() => models.find(item => item.id === model), [models, model]);

    const handleSave = async () => {
        setSaving(true);
        setError('');
        setMessage('');
        try {
            const payload = await apiFetch('/api/hopelab/settings', {
                method: 'POST',
                body: JSON.stringify({
                    apiKey: apiKey.trim(),
                    keepExistingKey: configured && !apiKey.trim(),
                    model: model.trim(),
                    embeddingModel: embeddingModel.trim(),
                    webSearchEnabled,
                    defaults: {
                        ...defaults,
                        yearMin: Number(defaults.yearMin),
                        yearMax: Number(defaults.yearMax),
                        maxResults: Number(defaults.maxResults),
                        randomRotationHours: Number(defaults.randomRotationHours),
                        excludedStudios: textToList(excludedStudios),
                        preferredStudios: textToList(preferredStudios)
                    }
                })
            });
            setConfigured(true);
            setMaskedApiKey(payload.maskedApiKey || '');
            setApiKey('');
            setModels(payload.models || []);
            setDefaults(current => ({ ...current, ...(payload.defaults || {}) }));
            setMessage(`Đã lưu HopeLab với model ID ${payload.model}${payload.supportsWebSearch ? ' · hỗ trợ Web Search' : ''}.`);
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteKey = async () => {
        if (!window.confirm('Gỡ Beeknoee API key khỏi HopeLab? Các tính năng research thường vẫn hoạt động, chỉ AI sẽ tắt.')) return;
        setSaving(true);
        setError('');
        try {
            await apiFetch('/api/hopelab/settings', { method: 'DELETE' });
            setConfigured(false);
            setMaskedApiKey('');
            setApiKey('');
            setModels([]);
            setMessage('Đã gỡ Beeknoee API key.');
        } catch (requestError) {
            setError(requestError.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <section className="hopelab-settings-card">
            <div className="hopelab-settings-header">
                <div className="hopelab-settings-icon"><Bot size={22} /></div>
                <div>
                    <div className="hopelab-settings-eyebrow">HOPELAB · ADMIN ONLY</div>
                    <h3>AI, Web Search & bộ lọc mặc định</h3>
                    <p>API key chỉ đi qua backend. Nhân viên không thể xem hoặc thay đổi cấu hình này.</p>
                </div>
                <div className={`hopelab-config-status ${configured ? 'is-ready' : ''}`}>
                    {configured ? <CheckCircle2 size={14} /> : <KeyRound size={14} />}
                    {configured ? 'Đã cấu hình' : 'Chưa có key'}
                </div>
            </div>

            {loading ? (
                <div className="hopelab-settings-loading"><RefreshCw size={16} className="animate-spin" /> Đang tải cấu hình HopeLab…</div>
            ) : (
                <div className="hopelab-settings-grid">
                    <div className="hopelab-settings-section">
                        <div className="hopelab-settings-section-title"><ShieldCheck size={16} /> Beeknoee API</div>
                        <label>
                            <span>API key</span>
                            <input
                                type="password"
                                autoComplete="off"
                                value={apiKey}
                                onChange={event => setApiKey(event.target.value)}
                                placeholder={configured ? `Đang dùng ${maskedApiKey} · để trống để giữ nguyên` : 'sk-bee-…'}
                            />
                        </label>
                        <label>
                            <span>Model ID phân tích</span>
                            <input list="hopelab-models" value={model} onChange={event => setModel(event.target.value)} placeholder="gemini-2.5-flash" />
                            <datalist id="hopelab-models">
                                {models.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                            </datalist>
                            <small>{selectedModel ? (selectedModel.supportsWebSearch ? 'Có trong catalog · hỗ trợ Web Search' : 'Có trong catalog · không có cờ Web Search') : 'Admin có thể nhập trực tiếp model ID do Beeknoee cung cấp.'}</small>
                        </label>
                        <label>
                            <span>Model ID embedding</span>
                            <input list="hopelab-embedding-models" value={embeddingModel} onChange={event => setEmbeddingModel(event.target.value)} placeholder="text-embedding-3-small" />
                            <datalist id="hopelab-embedding-models">
                                {models.filter(item => item.isEmbedding).map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
                            </datalist>
                            <small>Dùng để tìm sách tương tự theo nội dung. Nếu model không khả dụng, HopeLab tự quay về hybrid metadata.</small>
                        </label>
                        <label className="hopelab-settings-toggle">
                            <input type="checkbox" checked={webSearchEnabled} onChange={event => setWebSearchEnabled(event.target.checked)} />
                            <span><Globe2 size={15} /> Tự động dùng Web Search khi nhân viên tìm nội dung mới</span>
                        </label>
                    </div>

                    <div className="hopelab-settings-section">
                        <div className="hopelab-settings-section-title">Khoảng ưu tiên mặc định</div>
                        <div className="hopelab-settings-inline">
                            <label><span>Từ năm</span><input type="number" min="1900" max={new Date().getFullYear()} value={defaults.yearMin} onChange={event => setDefaults({ ...defaults, yearMin: event.target.value })} /></label>
                            <label><span>Đến năm</span><input type="number" min="1900" max={new Date().getFullYear()} value={defaults.yearMax} onChange={event => setDefaults({ ...defaults, yearMax: event.target.value })} /></label>
                        </div>
                        <div className="hopelab-settings-inline">
                            <label><span>Số kết quả</span><input type="number" min="6" max="30" value={defaults.maxResults} onChange={event => setDefaults({ ...defaults, maxResults: event.target.value })} /></label>
                            <label><span>Đổi vòng random</span><input type="number" min="1" max="72" value={defaults.randomRotationHours} onChange={event => setDefaults({ ...defaults, randomRotationHours: event.target.value })} /><small>giờ</small></label>
                        </div>
                    </div>

                    <div className="hopelab-settings-section">
                        <div className="hopelab-settings-section-title">Publisher blacklist</div>
                        <textarea value={excludedStudios} onChange={event => setExcludedStudios(event.target.value)} rows={6} />
                        <small>Mỗi publisher/studio một dòng. Bạn có thể bổ sung bất cứ lúc nào khi team gặp claim thường xuyên. “MountainPress” được chuẩn hóa về “Mountaindale Press”.</small>
                    </div>

                    <div className="hopelab-settings-section">
                        <div className="hopelab-settings-section-title">Publisher whitelist</div>
                        <textarea value={preferredStudios} onChange={event => setPreferredStudios(event.target.value)} rows={6} />
                        <small>Whitelist được cộng điểm ưu tiên; đây vẫn là heuristic nội bộ, không phải kết luận pháp lý về bản quyền.</small>
                    </div>
                </div>
            )}

            {error && <div className="hopelab-settings-message is-error">{error}</div>}
            {message && <div className="hopelab-settings-message is-success">{message}</div>}
            <div className="hopelab-settings-actions">
                {configured && <button type="button" className="hopelab-secondary-button is-danger" onClick={handleDeleteKey} disabled={saving}><Trash2 size={15} /> Gỡ API key</button>}
                <button type="button" className="hopelab-secondary-button" onClick={loadSettings} disabled={saving}><RefreshCw size={15} /> Tải lại</button>
                <button type="button" className="hopelab-settings-save-button" onClick={handleSave} disabled={saving || loading}><Save size={15} /> {saving ? 'Đang lưu…' : 'Lưu HopeLab'}</button>
            </div>
        </section>
    );
}
